/**
 * useChecklistSuggestions Hook
 *
 * Provides access to suggested checklist items for a document
 * and mutations to accept/reject/dismiss them.
 */

import { api } from "convex/_generated/api";
import { useMemo } from "react";
import type { Id } from "@/packages/types";
import { useApiMutation } from "@/packages/features-shared";
import { useWorkspaceQuery, useWorkspaceId } from "@/hooks";

export interface ResolvedSuggestion {
  checklistItemId: Id<"checklistItems">;
  score: number;
  confidence: string;
  reason: string;
  relevantExcerpt: string;
  title: string;
  categoryId: Id<"checklistCategories"> | null;
  categoryName: string;
  clauseRefs: string[];
  exampleDocuments: Array<{ name: string; content: string }>;
}

export function useChecklistSuggestions(
  documentId: Id<"documents"> | null | undefined,
) {
  const workspaceId = useWorkspaceId();
  const { data, isLoading } = useWorkspaceQuery(
    api.document_queries.getSuggestedChecklistDetails,
    { documentId: documentId ?? null },
  );

  const suggestions: ResolvedSuggestion[] = useMemo(() => {
    if (!data?.suggestions) return [];
    return data.suggestions;
  }, [data]);

  const acceptMutation = useApiMutation(
    api.document_mutations.acceptChecklistSuggestions,
    {
      name: "accept-checklist-suggestions",
      successMessage: "Checklist suggestions accepted",
    },
  );

  const dismissMutation = useApiMutation(
    api.document_mutations.dismissAllSuggestions,
    {
      name: "dismiss-checklist-suggestions",
      successMessage: "Suggestions dismissed",
    },
  );

  const acceptSuggestions = async (
    docId: Id<"documents">,
    acceptedIds: Id<"checklistItems">[],
    rejectedIds: Id<"checklistItems">[],
  ) => {
    await acceptMutation.mutateAsync({
      documentId: docId,
      acceptedItemIds: acceptedIds,
      rejectedItemIds: rejectedIds,
      workspaceId,
    });
  };

  const dismissAll = async (docId: Id<"documents">) => {
    await dismissMutation.mutateAsync({ documentId: docId, workspaceId });
  };

  return {
    suggestions,
    isLoading,
    acceptSuggestions,
    dismissAll,
    isAccepting: acceptMutation.isPending || dismissMutation.isPending,
  };
}
